import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TimelineModule } from 'primeng/timeline';
import { MessageModule } from 'primeng/message';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { WorkflowAvailableAction, WorkflowHistory, WorkflowInstance } from '../workflow.model';
import { WorkflowService } from '../workflow.service';

@Component({
    selector: 'app-workflow-panel',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        ProgressBarModule,
        TagModule,
        TextareaModule,
        TimelineModule,
        MessageModule
    ],
    templateUrl: './workflow-panel.component.html'
})
export class WorkflowPanelComponent implements OnChanges {
    @Input({ required: true }) resourceKey!: string;
    @Input({ required: true }) resourceId!: number;
    @Input() companyId?: number;
    @Input() context: Record<string, unknown> = {};

    instance?: WorkflowInstance;
    actions: WorkflowAvailableAction[] = [];
    history: WorkflowHistory[] = [];
    comment = '';
    isLoading = false;
    errorMessage = '';

    constructor(
        private readonly workflowService: WorkflowService,
        private readonly sessionStorageService: SessionStorageService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['resourceKey'] || changes['resourceId'] || changes['companyId']) && this.resourceKey && this.resourceId) {
            this.load();
        }
    }

    load(): void {
        const resolvedCompanyId = this.resolveCompanyId();
        if (!resolvedCompanyId) {
            this.errorMessage = 'Company is required to load workflow.';
            return;
        }
        this.isLoading = true;
        this.errorMessage = '';
        this.workflowService.getInstance(resolvedCompanyId, this.resourceKey, this.resourceId).subscribe({
            next: instance => {
                this.instance = instance;
                this.loadActionsAndHistory();
            },
            error: () => {
                this.instance = undefined;
                this.actions = [];
                this.history = [];
                this.isLoading = false;
                this.errorMessage = 'No workflow has been started for this record.';
            }
        });
    }

    startWorkflow(): void {
        const resolvedCompanyId = this.resolveCompanyId();
        if (!resolvedCompanyId) {
            this.errorMessage = 'Company is required to start workflow.';
            return;
        }
        this.isLoading = true;
        this.workflowService.startWorkflow({
            companyId: resolvedCompanyId,
            resourceKey: this.resourceKey,
            resourceId: this.resourceId,
            startedByCustomerId: this.sessionStorageService.getCustomer()?.id ?? null,
            context: this.context
        }).subscribe({
            next: instance => {
                this.instance = instance;
                this.comment = '';
                this.loadActionsAndHistory();
            },
            error: error => {
                this.isLoading = false;
                this.errorMessage = error?.error?.message ?? 'Unable to start workflow.';
            }
        });
    }

    perform(action: WorkflowAvailableAction): void {
        if (!this.instance) {
            return;
        }
        this.isLoading = true;
        this.errorMessage = '';
        this.workflowService.transition(this.instance.workflowInstanceId, {
            actionCode: action.actionCode,
            comment: this.comment,
            performedByCustomerId: this.sessionStorageService.getCustomer()?.id ?? null,
            hasAttachment: false,
            context: this.context
        }).subscribe({
            next: instance => {
                this.instance = instance;
                this.comment = '';
                this.loadActionsAndHistory();
            },
            error: error => {
                this.isLoading = false;
                this.errorMessage = error?.error?.message ?? 'Unable to perform workflow action.';
            }
        });
    }

    private loadActionsAndHistory(): void {
        if (!this.instance) {
            this.isLoading = false;
            return;
        }
        this.workflowService.getAvailableActions(
            this.instance.workflowInstanceId,
            this.sessionStorageService.getCustomer()?.id ?? null,
            this.context
        ).subscribe(actions => this.actions = actions);
        this.workflowService.getHistory(this.instance.workflowInstanceId).subscribe({
            next: history => {
                this.history = history;
                this.isLoading = false;
            },
            error: () => this.isLoading = false
        });
    }

    private resolveCompanyId(): number | undefined {
        return this.companyId ?? this.sessionStorageService.getCompany()?.id;
    }
}
